import { Button, DropdownDivider, DropdownItem, HFlow, Icon, modal, Tooltip } from 'bold-ui'
import { purple } from 'bold-ui/lib/styles/colors'
import { useAlert } from 'components/alert'
import CheckPermission from 'components/auth/CheckPermission'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { DropdownButton } from 'components/dropdown'
import { useErrorHandler } from 'components/error'
import { confirm } from 'components/modals/confirm'
import { useFlags } from 'config/useFlagsContext'
import { css } from 'emotion'
import {
  useAtenderMutation,
  useCancelarAusenciaMutation,
  useExcluirAtendimentoMutation,
  useInformarAusenciaMutation,
  useRealizarAtendimentoVacinacaoMutation,
  useRealizarEscutaInicialMutation,
  useSalvarJustificativaAutomaticaVisualizarEscutaInicialMutation,
  useTornarLotacaoAtualResponsavelAtendimentoObservacaoMutation,
} from 'graphql/hooks.generated'
import { AtendimentoActions, StatusAtendimento, StatusRevisaoAtendimento } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useLoginGovBr } from 'hooks/useLoginGovBr'
import { ReactComponent as IconRevisao } from 'images/atendimento/icon-revisao.svg'
import { ReactComponent as IconAddProfessionalOutlined } from 'images/lista-atendimento/icon-add-professional-outlined.svg'
import React, { useEffect } from 'react'
import { useHistory } from 'react-router'
import Permissions from 'types/Permissions'
import { grupoCboVisualizarInformacoesCidadao } from 'view/cidadao/acessos'

import { TornarResponsavelModal } from './actions/tornar-responsavel/TornarResponsavelModal'
import { useListaAtendimentoActionsLoading } from './hooks/useListaAtendimentoActionsLoading'

export interface ListaAtendimentoItemActionsProps {
  actions: AtendimentoActions
  responsavelId: ID
  atendimentoId: ID
  cidadaoId: ID
  prontuarioId: ID
  statusAtendimento: StatusAtendimento
  statusRevisaoAtendimento: StatusRevisaoAtendimento
  isAtendimentoObservacao: boolean
}

export default function ListaAtendimentoItemActions(props: ListaAtendimentoItemActionsProps) {
  const {
    actions,
    atendimentoId,
    responsavelId,
    cidadaoId,
    prontuarioId,
    statusAtendimento,
    isAtendimentoObservacao,
  } = props
  const pathCidadao = `/cidadao/${cidadaoId}`
  const { acesso } = useAcessoLotacaoOrEstagio()
  const { analytics } = useFirebase()

  const history = useHistory()
  const alert = useAlert()
  const [atender, { loading: isLoadingAtender }] = useAtenderMutation()
  const [realizarEscutaInicial, { loading: isLoadingEscutaInicial }] = useRealizarEscutaInicialMutation()
  const [vacinacao, { loading: isLoadingVacinacao }] = useRealizarAtendimentoVacinacaoMutation()
  const handleRejection = useErrorHandler()
  const [
    salvarJustificativaAutomaticaVisualizarEscutaInicial,
    { loading: isLoadingSalvarJustificativaAutomaticaVisualizarEscutaInicial },
  ] = useSalvarJustificativaAutomaticaVisualizarEscutaInicialMutation()
  const [informarAusencia, { loading: isLoadingInformarAusencia }] = useInformarAusenciaMutation()
  const [cancelarAusencia, { loading: isLoadingCancelarAusencia }] = useCancelarAusenciaMutation()
  const [excluir, { loading: isLoadingExcluir }] = useExcluirAtendimentoMutation()
  const [
    tornarLotacaoAtualResponsavelAtendimentoObservacao,
    { loading: isLoadingTornarResponsavelObservacao },
  ] = useTornarLotacaoAtualResponsavelAtendimentoObservacaoMutation()
  const { isActionsLoading, startLoading, stopLoading } = useListaAtendimentoActionsLoading()
  const { PROGRAMA_DIGNIDADE_MENSTRUAL } = useFlags()
  const { isGovBrEnabled } = useLoginGovBr()

  const onAtenderClick = () => {
    return atender({ variables: { atendimento: atendimentoId } })
      .then((res) => {
        history.push(`/lista-atendimento/atendimento/${atendimentoId}`)
        if (isAtendimentoObservacao) {
          analytics.logEvent('atender_cidadão_OBS')
        }
        return res
      })
      .catch(handleRejection)
  }

  const onAtenderVacinacao = () => {
    return vacinacao({ variables: { atendimento: atendimentoId } })
      .then(() => {
        history.push(`/lista-atendimento/atendimento-vacinacao/${atendimentoId}`)
      })
      .catch(handleRejection)
  }

  const onEscutaInicialClick = () => {
    analytics.logEvent('realizar_escuta_inicial_EI')
    return realizarEscutaInicial({ variables: { atendimento: atendimentoId } })
      .then((res) => {
        history.push(`/lista-atendimento/escuta-inicial/${atendimentoId}`)
        return res
      })
      .catch(handleRejection)
  }

  const onVisualizarProntuarioClick = () => history.push(`${pathCidadao}/folha-rosto`)

  const onVisualizarInformacoesCidadaoClick = () => history.push(`${pathCidadao}/informacoes`)

  const onInformarAusencia = () => {
    return informarAusencia({ variables: { atendimento: atendimentoId } })
      .then((res) => {
        alert('success', 'Ausência do cidadão informada.')
      })
      .catch(handleRejection)
  }

  const onCancelarAusencia = () => {
    return cancelarAusencia({ variables: { atendimento: atendimentoId } })
      .then((res) => {
        alert('success', 'Cidadão foi reinserido com sucesso.')
      })
      .catch(handleRejection)
  }

  const onGerarDeclaracao = () => {
    startLoading()
    return history.push(`/lista-atendimento/declaracaoComparecimento/${atendimentoId}?callbackUrl=lista-atendimento`)
  }

  const handleGerarAutorizacaoAbsorventes = () => {
    startLoading()
    analytics.logEvent('gerar_autorizacao_absorventes_la')
    return history.push(`/lista-atendimento/cidadao/${cidadaoId}/autorizacao-absorventes`)
  }

  const onVisualizarAtendimentosDia = () => {
    startLoading()
    return history.push(`/lista-atendimento/atendimentoDoDia/${atendimentoId}`)
  }

  const onVisualizarEscutaInicialClick = () => {
    analytics.logEvent('visualizar_escuta_inicial_EI')
    return salvarJustificativaAutomaticaVisualizarEscutaInicial({
      variables: { prontuarioId },
    })
      .then(() => {
        history.push(`/lista-atendimento/visualizar-escuta/${atendimentoId}`)
      })
      .catch(handleRejection)
  }

  const onEditarClick = () => {
    startLoading()
    return history.push(`/lista-atendimento/editar/${atendimentoId}`)
  }

  const onExcluirClick = () => {
    confirm({
      title: 'Deseja realmente excluir o registro de atendimento?',
      type: 'danger',
      confirmLabel: 'Excluir',
      onConfirm: () => {
        excluir({ variables: { atendimento: atendimentoId } })
          .then((res) => {
            alert('success', 'Registro excluído com sucesso.')
            analytics.logEvent('excluir_cidadao_LA')
          })
          .catch(handleRejection)
      },
    })()
  }

  const tornarResponsavel = () => {
    return tornarLotacaoAtualResponsavelAtendimentoObservacao({ variables: { atendimentoId } })
      .then((res) => {
        alert('success', 'Usuário foi inserido como responsável deste atendimento de observação.')
        analytics.logEvent('tornar_responsavel_OBS')
      })
      .catch(handleRejection)
  }

  const onTornarResponsavelObservacaoClick = () => {
    modal({
      title: 'Deseja tornar-se o profissional responsável desta observação no sistema?',
      icon: {
        name: 'questionMarkOutline',
        fill: 'primary',
      },
      size: 'small',
      depthLevel: 5,
      render: () => <TornarResponsavelModal />,
      actions: [{ label: 'Cancelar' }, { label: 'Confirmar', onClick: tornarResponsavel, kind: 'primary' }],
    })()
  }

  useEffect(() => {
    stopLoading()
  })

  const isRevisarRetificar = !!actions.revisarRetificar
  const isEscutaInicialEnabled = !!actions.escutaInicial?.enabled
  const isVacinacaoEnabled = !!actions.vacinacao?.enabled
  const isAtenderEnabled = !!actions.atender?.enabled
  const isRevisarRetificarEscutaPreAtendimento = isEscutaInicialEnabled && isRevisarRetificar
  const isRevisarRetificarVacinacao = !isEscutaInicialEnabled && isVacinacaoEnabled && isRevisarRetificar
  const isRevisarRetificarAtendimento =
    !isEscutaInicialEnabled && !isVacinacaoEnabled && isAtenderEnabled && isRevisarRetificar
  const isAnyLoading =
    isLoadingAtender ||
    isLoadingEscutaInicial ||
    isLoadingVacinacao ||
    isLoadingSalvarJustificativaAutomaticaVisualizarEscutaInicial ||
    isLoadingInformarAusencia ||
    isLoadingCancelarAusencia ||
    isLoadingExcluir ||
    isLoadingTornarResponsavelObservacao ||
    isActionsLoading

  const tooltipAcaoEmProgresso = 'Outra ação está em progresso.'

  return (
    <HFlow hSpacing={0.25} style={{ marginRight: '0.5rem', marginLeft: 'auto' }}>
      {!actions.tornarResponsavelObservacao ? (
        actions.escutaInicial ? (
          isRevisarRetificarEscutaPreAtendimento ? (
            <RevisarRetificarButton
              hint={actions.revisarRetificar.hint}
              disabled={!actions.revisarRetificar.enabled || isAnyLoading}
              onClick={onEscutaInicialClick}
            />
          ) : (
            <Tooltip text={isAnyLoading ? tooltipAcaoEmProgresso : actions.escutaInicial?.hint}>
              <Button
                size='small'
                kind='normal'
                skin='ghost'
                onClick={onEscutaInicialClick}
                disabled={!actions.escutaInicial?.enabled || isAnyLoading}
              >
                <Icon icon='nurse' />
              </Button>
            </Tooltip>
          )
        ) : (
          <CheckPermission permission={Permissions.visualizarListaDeAtendimento.visualizarEscutaInicial}>
            <Tooltip text={isAnyLoading ? tooltipAcaoEmProgresso : actions.visualizarEscutaInicial?.hint}>
              <Button
                size='small'
                kind='normal'
                skin='ghost'
                onClick={onVisualizarEscutaInicialClick}
                disabled={!actions.visualizarEscutaInicial?.enabled || isAnyLoading}
              >
                <Icon icon='nurseView' />
              </Button>
            </Tooltip>
          </CheckPermission>
        )
      ) : (
        <Tooltip text={isAnyLoading ? tooltipAcaoEmProgresso : actions.tornarResponsavelObservacao.hint}>
          <Button
            size='small'
            kind='normal'
            skin='ghost'
            onClick={onTornarResponsavelObservacaoClick}
            disabled={!actions.tornarResponsavelObservacao.enabled || isAnyLoading}
          >
            <IconAddProfessionalOutlined />
          </Button>
        </Tooltip>
      )}

      {actions.vacinacao &&
        (isRevisarRetificarVacinacao ? (
          <RevisarRetificarButton
            hint={actions.revisarRetificar.hint}
            disabled={!actions.revisarRetificar.enabled || isAnyLoading}
            onClick={onAtenderVacinacao}
          />
        ) : (
          <Tooltip text={isAnyLoading ? tooltipAcaoEmProgresso : actions.vacinacao.hint}>
            <Button
              size='small'
              kind='normal'
              skin='ghost'
              onClick={onAtenderVacinacao}
              disabled={!actions.vacinacao.enabled || isAnyLoading}
            >
              <Icon icon='needleOutline' />
            </Button>
          </Tooltip>
        ))}

      {!actions.vacinacao &&
        actions.atender &&
        (isRevisarRetificarAtendimento ? (
          <RevisarRetificarButton
            hint={actions.revisarRetificar.hint}
            disabled={!actions.revisarRetificar.enabled || isAnyLoading}
            onClick={onAtenderClick}
          />
        ) : (
          <Tooltip text={isAnyLoading ? tooltipAcaoEmProgresso : actions.atender.hint}>
            <Button
              size='small'
              kind='normal'
              skin='ghost'
              onClick={onAtenderClick}
              disabled={!actions.atender.enabled || isAnyLoading}
            >
              <Icon icon='multipleUsersOutline' />
            </Button>
          </Tooltip>
        ))}

      <Tooltip text='Mais opções'>
        <DropdownButton icon='dots'>
          {actions.tornarResponsavelObservacao && (
            <CheckPermission permission={Permissions.visualizarListaDeAtendimento.visualizarEscutaInicial}>
              <Tooltip text={isAnyLoading ? tooltipAcaoEmProgresso : actions.visualizarEscutaInicial?.hint}>
                <DropdownItem
                  disabled={!actions.visualizarEscutaInicial?.enabled || isAnyLoading}
                  onClick={onVisualizarEscutaInicialClick}
                >
                  Visualizar escuta inicial
                </DropdownItem>
              </Tooltip>
            </CheckPermission>
          )}
          {actions.vacinacao && actions.atender && (
            <Tooltip text={!actions.atender.enabled && isAnyLoading ? tooltipAcaoEmProgresso : actions.atender?.hint}>
              <DropdownItem onClick={onAtenderClick} disabled={!actions.atender.enabled || isAnyLoading}>
                {statusAtendimento === 'EM_ATENDIMENTO' && !actions.vacinacao.enabled && responsavelId === acesso?.id
                  ? 'Continuar atendimento'
                  : 'Realizar atendimento'}
              </DropdownItem>
            </Tooltip>
          )}

          <CheckPermission permission={Permissions.visualizarListaDeAtendimento.informarQueCidadaoNaoAguardou}>
            {actions.cancelarAusencia ? (
              <Tooltip text={isAnyLoading ? tooltipAcaoEmProgresso : actions.cancelarAusencia?.hint}>
                <DropdownItem
                  disabled={!actions.cancelarAusencia?.enabled || isAnyLoading}
                  onClick={onCancelarAusencia}
                >
                  Cidadão retornou
                </DropdownItem>
              </Tooltip>
            ) : (
              <Tooltip text={isAnyLoading ? tooltipAcaoEmProgresso : actions.informarAusencia?.hint}>
                <DropdownItem
                  disabled={!actions.informarAusencia?.enabled || isAnyLoading}
                  onClick={onInformarAusencia}
                >
                  Cidadão não aguardou
                </DropdownItem>
              </Tooltip>
            )}
          </CheckPermission>

          {actions.declaracaoComparecimento && (
            <DropdownItem
              disabled={!actions.declaracaoComparecimento.enabled || isAnyLoading}
              onClick={onGerarDeclaracao}
            >
              Gerar declaração de comparecimento
            </DropdownItem>
          )}

          {PROGRAMA_DIGNIDADE_MENSTRUAL && isGovBrEnabled && actions.gerarAutorizacaoRetiradaAbsorventes && (
            <Tooltip text={isAnyLoading ? tooltipAcaoEmProgresso : actions.gerarAutorizacaoRetiradaAbsorventes.hint}>
              <DropdownItem
                disabled={!actions.gerarAutorizacaoRetiradaAbsorventes.enabled || isAnyLoading}
                onClick={handleGerarAutorizacaoAbsorventes}
              >
                Gerar autorização para retirada de absorventes
              </DropdownItem>
            </Tooltip>
          )}

          {actions.visualizarProntuario && (
            <DropdownItem
              disabled={!actions.visualizarProntuario.enabled || isAnyLoading}
              onClick={onVisualizarProntuarioClick}
            >
              Visualizar prontuário
            </DropdownItem>
          )}

          {actions.atendimentosDia && (
            <Tooltip text={isAnyLoading ? tooltipAcaoEmProgresso : actions.atendimentosDia?.hint}>
              <DropdownItem
                disabled={!actions.atendimentosDia.enabled || isAnyLoading}
                onClick={onVisualizarAtendimentosDia}
              >
                Visualizar atendimentos do dia
              </DropdownItem>
            </Tooltip>
          )}

          <CheckPermission
            hasAnyCbo={[grupoCboVisualizarInformacoesCidadao]}
            hasAnyPermission={[Permissions.gestaoDeCadastrosDeCidadao.visualizarCidadao]}
          >
            <DropdownItem onClick={onVisualizarInformacoesCidadaoClick} disabled={isAnyLoading}>
              Visualizar informações do cidadão
            </DropdownItem>
          </CheckPermission>

          {actions.editar && (
            <Tooltip text={isAnyLoading ? tooltipAcaoEmProgresso : actions.editar?.hint}>
              <DropdownItem disabled={!actions.editar.enabled || isAnyLoading} onClick={onEditarClick}>
                Editar
              </DropdownItem>
            </Tooltip>
          )}

          {actions.excluir && (
            <>
              <DropdownDivider />

              <Tooltip text={isAnyLoading ? tooltipAcaoEmProgresso : actions.excluir?.hint}>
                <DropdownItem
                  type='danger'
                  disabled={!actions.excluir.enabled || isAnyLoading}
                  onClick={onExcluirClick}
                >
                  <HFlow alignItems='center' hSpacing={0.5}>
                    <Icon icon='trashOutline' />
                    Excluir
                  </HFlow>
                </DropdownItem>
              </Tooltip>
            </>
          )}
        </DropdownButton>
      </Tooltip>
    </HFlow>
  )
}

interface RevisarRetificarButtonProps {
  onClick: () => void
  disabled: boolean
  hint: string
}

const RevisarRetificarButton = ({ onClick, disabled, hint }: RevisarRetificarButtonProps) => (
  <Tooltip text={hint}>
    <Button
      style={css`
        background-color: ${purple.c90};
      `}
      size='small'
      kind='normal'
      skin='ghost'
      onClick={onClick}
      disabled={disabled}
    >
      <IconRevisao
        className={css`
          fill: ${purple.c20};
        `}
      />
    </Button>
  </Tooltip>
)
